import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ShoppingBag, X } from 'lucide-react';
import { previewPurchases, recentPurchases } from '../data/recentPurchases';
const INITIAL_DELAY_MS = 5000;
const VISIBLE_MS = 5000;
const GAP_MS = 4000;
export function PurchaseNotification() {
  const purchases = useMemo(() => recentPurchases.length > 0 ? recentPurchases : isEditorPreview() ? previewPurchases : [], []);
  const [index, setIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (isDismissed || purchases.length === 0) return;
    const delay = isVisible ? VISIBLE_MS : hasStarted ? GAP_MS : INITIAL_DELAY_MS;
    const timer = window.setTimeout(() => {
      if (isVisible) {
        setIsVisible(false);
        setIndex(current => (current + 1) % purchases.length);
      } else {
        setHasStarted(true);
        setIsVisible(true);
      }
    }, delay);
    return () => window.clearTimeout(timer);
  }, [isVisible, isDismissed, hasStarted, purchases.length]);
  if (isDismissed || purchases.length === 0) return null;
  const purchase = purchases[index];
  const minutesLabel = `${purchase.minutesAgo} minute${purchase.minutesAgo === 1 ? '' : 's'} ago`;
  return <div className="fixed bottom-4 left-4 z-40 w-[320px] max-w-[calc(100vw-2rem)] pointer-events-none" aria-live="polite" data-id="element-266" data-mp="19nbsroo2p7eh">
      <AnimatePresence data-id="element-267" data-mp="zrqwm75or1cn">
        {isVisible && <motion.div key={`${purchase.firstName}-${purchase.city}-${index}`} initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto relative flex items-center gap-3.5 bg-white rounded-2xl border border-neutral-200 shadow-xl shadow-neutral-900/20 py-3 pl-3 pr-9 overflow-hidden" role="status" data-id="element-268" data-mp="zt1yg1fklz14">
            <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-amber-500" aria-hidden="true" data-id="element-269" data-mp="2nmnco21twswl" />

            <div className="relative shrink-0 w-11 h-11 rounded-xl bg-yellow-50 text-amber-600 flex items-center justify-center" data-id="element-270" data-mp="3gdf4yofrwxen">
              <ShoppingBag size={19} strokeWidth={2.2} data-id="element-271" data-mp="3bfndfvj1jdaz" />
            </div>

            <div className="min-w-0 flex-1" data-id="element-272" data-mp="37cqta0dur94x">
              <p className="flex items-center gap-1.5 text-sm text-neutral-900 leading-5 whitespace-nowrap" data-id="element-273" data-mp="37bfn5twxppan">
                <img src={`https://flagcdn.com/${purchase.countryCode}.svg`} alt={`${purchase.country} flag`} className="w-[18px] h-[13px] rounded-[2px] object-cover shrink-0 ring-1 ring-black/5" data-id="element-274" data-mp="32dmjp8n0n9e2" />
                <span className="font-bold" data-id="element-275" data-mp="6hrcstb80v5i">{purchase.firstName}</span>
                <span className="text-neutral-800/70 truncate" data-id="element-276" data-mp="bfjdoctufrlu">
                  from {purchase.city}, {purchase.country}
                </span>
              </p>
              <p className="text-sm text-neutral-800/70 leading-5" data-id="element-277" data-mp="ffa1q8rzjtmo">
                purchased{' '}
                <span className="font-semibold text-neutral-900" data-id="element-278" data-mp="ka7k46b5nfc8">Heavy Equipment Instructor Atlas</span>
              </p>
              <p className="flex items-center gap-1.5 text-xs text-neutral-800/60 leading-4 mt-1" data-id="element-279" data-mp="u6ajx5ijb1w0">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" aria-hidden="true" data-id="element-280" data-mp="z5dq4ta93e5k" />
                {minutesLabel}
              </p>
            </div>

            <button type="button" onClick={() => setIsDismissed(true)} className="absolute top-2 right-2 w-6 h-6 rounded-full text-neutral-800/40 hover:text-neutral-900 hover:bg-neutral-100 flex items-center justify-center transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" aria-label="Close notification" data-id="element-281" data-mp="136fjwuleey05">
              <X size={13} data-id="element-282" data-mp="2jm36n12kcyrz" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function isEditorPreview(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}